import { ListDataParams, ListDataResponse } from '@api-client/types';
import {
  GetPermissionInterface,
  PermissionInterface,
  RoleAndPermissionsInterface,
  RoleInfoInterface,
  RoleInterface,
  RolePermissionFormInterface,
  RoleUserInterface,
  RoleWithPermissionsInterface
} from '@interfaces/RolesAndPermissions.interface';
import BaseApiClient from '../BaseApiClient';

export class ApiRolesAndPermissions extends BaseApiClient {
  async getRoles(): Promise<RoleInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/role')
        .then(({ data }) => {
          resolve(data.data);
        })
        .catch((err) => reject(err));
    });
  }

  async getRolesInfo(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<RoleInfoInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList('/role/info', listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async createRole(
    roleAndPermissions: RolePermissionFormInterface
  ): Promise<RoleInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/role', { ...roleAndPermissions })
        .then(({ data }) => {
          resolve(data.data);
        })
        .catch((err) => reject(err));
    });
  }

  async showRole(id: string): Promise<RoleInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/role/${id}`)
        .then(({ data }) => {
          resolve(data);
        })
        .catch((err) => reject(err));
    });
  }

  async getRoleUsers(
    roleId: string,
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<RoleUserInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList(`/role/${roleId}/users`, listData)
        .then((res) => {
          if (res.data) {
            const items = (res.data.data || []).map(
              (row: Omit<RoleUserInterface, 'id'>) => ({
                ...row,
                id: String(row.user_id ?? row.team_invitation_uuid ?? row.email)
              })
            );
            resolve({
              items,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async updateRole(
    id: string,
    roleAndPermission: RolePermissionFormInterface
  ): Promise<RoleInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/role/${id}`, { ...roleAndPermission })
        .then(({ data }) => {
          resolve(data.data);
        })
        .catch((err) => reject(err));
    });
  }

  async updateUserRole(
    userId: number | string,
    roleId: number | string
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/users/${userId}/roles`, { role_id: roleId })
        .then(() => {
          resolve();
        })
        .catch((err) => reject(err));
    });
  }

  async deleteRole(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(`/role/${id}`)
        .then(() => {
          resolve();
        })
        .catch((err) => reject(err));
    });
  }

  async deleteRoles(roleIds: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete('/role', { data: { data: roleIds } })
        .then(() => {
          resolve();
        })
        .catch((err) => reject(err));
    });
  }

  async getRolePermissions(id: string): Promise<RoleAndPermissionsInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get<RoleWithPermissionsInterface>(`/role/${id}/permission`)
        .then(({ data }) => {
          const normalisedData = {
            name: data.role.name,
            description: data.role.description,
            permissions: data.role_permissions.map((permission) => {
              return {
                ...permission.permission,
                permission_group_name:
                  permission.permission.permission_group.name,
                permission_group_id: permission.permission.permission_group.id,
                has_permission: permission.has_permission,
                can_be_false: permission.can_be_removed
              };
            })
          };
          resolve(normalisedData);
        })
        .catch((err) => reject(err));
    });
  }

  async updateRolePermissions(
    id: string,
    permissions: PermissionInterface[]
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/role/${id}/permission`, { permissions })
        .then(() => {
          resolve();
        })
        .catch((err) => reject(err));
    });
  }

  async getPermissions(): Promise<PermissionInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get<{ data: GetPermissionInterface[] }>('/permission')
        .then(({ data }) => {
          const testing = data.data.map((permission) => {
            return {
              ...permission,
              permission_group_name: permission.permission_group.name,
              permission_group_id: permission.permission_group.id,
              has_permission: false,
              can_be_false: true
            };
          });
          resolve(testing);
        })
        .catch((err) => reject(err));
    });
  }

  async getPermission(id: string): Promise<PermissionInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/permission/${id}`)
        .then(({ data }) => {
          resolve(data.data);
        })
        .catch((err) => reject(err));
    });
  }
}

export { rolesAndPermissionsQueryKeys } from './query-keys';
export type {
  RolesAndPermissionsRoleUsersListParams,
  RolesAndPermissionsRolesInfoListParams
} from './query-keys';
export {
  useRolesAndPermissionsPermissionQuery,
  useRolesAndPermissionsPermissionsQuery,
  useRolesAndPermissionsRolePermissionsQuery,
  useRolesAndPermissionsRoleQuery,
  useRolesAndPermissionsRoleUsersListQuery,
  useRolesAndPermissionsRolesInfoListQuery,
  useRolesAndPermissionsRolesQuery
} from './use-roles-and-permissions-queries';
export {
  useRolesAndPermissionsCreateRoleMutation,
  useRolesAndPermissionsDeleteRoleMutation,
  useRolesAndPermissionsDeleteRolesMutation,
  useRolesAndPermissionsUpdateRoleMutation,
  useRolesAndPermissionsUpdateRolePermissionsMutation,
  useRolesAndPermissionsUpdateUserRoleMutation
} from './use-roles-and-permissions-mutations';
export type {
  RolesAndPermissionsUpdateRolePermissionsVariables,
  RolesAndPermissionsUpdateRoleVariables,
  RolesAndPermissionsUpdateUserRoleVariables
} from './use-roles-and-permissions-mutations';
